import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IdentityService } from 'src/app/@core/services/identity.service';
import { PageType } from 'src/app/static-pages/enums/page-type.enum';
import { PageModel } from 'src/app/static-pages/model/page.model';
import { PagesService } from 'src/app/static-pages/service/pages.service';
import { environment } from 'src/environments/environment';
import { IdentityModel } from 'src/app/@core/models/identity.model';
import { LanguageService } from 'src/app/services/language.service';
import { LanguageEnum } from '../../models/item.model';
import { Status } from 'src/app/@core/enums/status.enum';
import { buildFooterGroups, PortalLink, PortalLinkContext, PortalLinkGroup } from 'src/app/@core/navigation/portal-links';
import { PortalNavigationService } from 'src/app/@core/navigation/portal-navigation.service';
import { ContactService } from 'src/app/static-pages/service/contact.service';
import { ContactSettingModel } from 'src/app/static-pages/model/contact-setting.model';
import { ContactType } from 'src/app/static-pages/enums/contact-type.enum';
import { TranslateService } from '@ngx-translate/core';

interface FooterSocialLink {
  labelKey: string;
  icon: string;
  url: string;
}

const SOCIAL_TYPES: Array<{ type: ContactType; labelKey: string; icon: string }> = [
  { type: ContactType.LinkedIn, labelKey: 'unifiedShell.footer.social.linkedIn', icon: 'fa-linkedin-in' },
  { type: ContactType.Twitter, labelKey: 'unifiedShell.footer.social.twitter', icon: 'fa-x-twitter' },
  { type: ContactType.Instagram, labelKey: 'unifiedShell.footer.social.instagram', icon: 'fa-instagram' },
  { type: ContactType.Facebook, labelKey: 'unifiedShell.footer.social.facebook', icon: 'fa-facebook-f' },
];

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit, OnDestroy {
  identityModel: IdentityModel = {} as IdentityModel;
  pages: PageModel[] = [];
  identityPrefix = environment.identityResource;
  isRtl: boolean;
  pageType = PageType;

  // Unified Code footer (criterion 27)
  unified = false;
  footerGroups: PortalLinkGroup[] = [];
  contactInfo: ContactSettingModel[] = [];
  emails: string[] = [];
  phones: string[] = [];
  socialLinks: FooterSocialLink[] = [];
  private destroySubject: Subject<void> = new Subject();

  constructor(
    private identityService: IdentityService,
    private pagesService: PagesService,
    private _LanguageService: LanguageService,
    private portalNavigationService: PortalNavigationService,
    private contactService: ContactService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.isRtl = this._LanguageService.getCurrentLanguage() === LanguageEnum.ar;
    this._LanguageService.languageChange.subscribe(
      (result: LanguageEnum) => (this.isRtl = result === LanguageEnum.ar)
    );
    this.identityModel = this.identityService.getIdentityModel();
    this.unified = this.identityService.isUnifiedCode();
    if (this.unified) {
      this.loadUnifiedFooter();
    } else {
      this.getPages();
    }
  }

  private loadUnifiedFooter(): void {
    this.portalNavigationService.context$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((context: PortalLinkContext) => (this.footerGroups = buildFooterGroups(context)));

    this.contactService
      .GetContactSettings()
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (result: ContactSettingModel[]) => {
          this.contactInfo = Array.isArray(result) ? result : [];
          this.emails = this.contactValues(ContactType.Email);
          this.phones = this.contactValues(ContactType.Phone);
          this.socialLinks = SOCIAL_TYPES.map((social) => ({
            labelKey: social.labelKey,
            icon: social.icon,
            url: this.toExternalUrl(this.contactValues(social.type)[0]),
          })).filter((social) => !!social.url);
        },
        error: () => (this.contactInfo = []),
      });
  }

  get organizationName(): string {
    const model = this.identityModel ?? ({} as IdentityModel);
    return (this.isRtl ? model.organizationNameAr || model.organizationName : model.organizationName || model.organizationNameAr) || '';
  }

  get footerLogo(): string {
    const logo = this.identityModel?.image || this.identityModel?.coloredImage;
    return logo ? this.identityPrefix + logo : 'assets/imgs/EXAB.svg';
  }

  get address(): string {
    const [ar] = this.contactValues(ContactType.AddressAr);
    const [en] = this.contactValues(ContactType.AddressEn);
    return (this.isRtl ? ar || en : en || ar) ?? '';
  }

  get hasContactInfo(): boolean {
    return !!this.address || this.emails.length > 0 || this.phones.length > 0;
  }

  get copyright(): string {
    const custom = this.isRtl ? this.identityModel?.footerAr : this.identityModel?.footer;
    return custom || this.translateService.instant('footer.rights');
  }

  groupHeading(group: PortalLinkGroup): string {
    return (this.isRtl ? group.headingAr || group.heading : group.heading || group.headingAr) ?? '';
  }

  linkText(link: PortalLink): string {
    return (this.isRtl ? link.titleAr || link.title : link.title || link.titleAr) ?? '';
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  phoneHref(phone: string): string {
    return (phone ?? '').replace(/[^\d+]/g, '');
  }

  changeLanguage(): void {
    this._LanguageService.toggle();
  }

  private contactValues(type: ContactType): string[] {
    return this.contactInfo
      .filter((contact) => contact.contactType === type && !!contact.title?.trim())
      .map((contact) => contact.title.trim());
  }

  /** Social links are stored as free text; only web addresses are rendered (a scheme is added when missing). */
  private toExternalUrl(value: string | undefined): string {
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    if (/^[\w-]+(\.[\w-]+)+(\/.*)?$/.test(value)) return `https://${value}`;
    return '';
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }


  getPages() {
    this.pagesService.getPages().subscribe((result: PageModel[]) => {
      this.pages = result;
    });
  }

  showPage(type: PageType) : boolean {
    if(this.pages.length === 0) return false;
    return  this.pages.some((c) => c.type === type && c.status === Status.Active && (c.contentAr?.length > 0 && c.content?.length > 0));

  }

  getCustomParentPages() {
    if(this.pages.length === 0) return;

    return this.pages.filter(c => c.type === PageType.Custom && c.status === Status.Active && !c.parentId);
  }

  getCustomChildPages(parentId: number) {
    if(this.pages.length === 0) return;

    return this.pages.filter(c => c.type === PageType.Custom && c.status === Status.Active && c.parentId === parentId);
  }
}
